import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { chunk } from 'lodash';
import useSWR from 'swr';

const apiClient = RacemapAPIClient.fromWindowLocation();
const BATCH_SIZE = 50;

/**
 * Hook to fetch read counts for a list of chip IDs/starters.
 * It batches requests to avoid hitting URL length limits on the server.
 * I tried to increase the URL length limit but this was not successful
 */
export function useReadCountsByTransponderIds(
  eventId: string | undefined | null,
  chipIds: Array<string>,
): Map<string, number> {
  const { data: readCountsByTransponderIds = new Map() } = useSWR<Map<string, number>>(
    eventId && chipIds.length > 0
      ? ['readCountsByStarters', eventId, chipIds.slice().sort()]
      : null,
    async () => {
      if (!eventId) return new Map();

      // Batch the chip IDs to avoid extremely long query strings
      const batches = chunk(chipIds, BATCH_SIZE);

      const results = await Promise.all(
        batches.map((batchIds) =>
          apiClient.prediction().getAllReadsByStartersCount({
            eventId: eventId,
            chipIds: batchIds,
          }),
        ),
      );

      // Merge all results directly into a Map
      const map = new Map<string, number>();
      results.forEach((result) => {
        Object.entries(result).forEach(([key, value]) => {
          map.set(key, value);
        });
      });
      return map;
    },
    {
      refreshInterval: OneMinuteInMillis,
      keepPreviousData: false,
    },
  );

  return readCountsByTransponderIds;
}
